import { db, auth, collection, addDoc, serverTimestamp, doc, getDoc, setDoc } from '@core/firebase';
import { 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    signOut, 
    onAuthStateChanged,
    sendPasswordResetEmail,
    reauthenticateWithCredential,
    EmailAuthProvider
} from 'firebase/auth';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';



import { AuthContext } from './AuthContext';

const authReducer = (state, action) => {
    switch (action.type) {
        case 'AUTH_START':
            return { ...state, loading: true, authError: null };
        case 'AUTH_SUCCESS':
            return { ...state, loading: false, user: action.payload, authError: null };
        case 'AUTH_ERROR':
            return { ...state, loading: false, authError: action.payload };
        case 'LOGOUT':
            return { ...state, user: null, authError: null, loading: false, isTerminalMode: false, terminalZone: null };
        case 'SET_TERMINAL_MODE':
            return { ...state, isTerminalMode: action.payload.active, terminalZone: action.payload.zone || null };
        default:
            return state;
    }
};

const initialAuthState = {
    user: null,
    loading: true,
    authError: null,
    isTerminalMode: localStorage.getItem('isTerminalMode') === 'true',
    terminalZone: localStorage.getItem('terminalZone') || null
};

/**
 * AuthProvider - Gestión de Sesión Real con Firebase + Roles Firestore.
 */
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = React.useReducer(authReducer, initialAuthState);

    /**
     * setTerminalMode
     * Activa el modo kiosko para una zona específica.
     */
    const setTerminalMode = (active, zone = null) => {
        localStorage.setItem('isTerminalMode', active ? 'true' : 'false');
        if (zone) localStorage.setItem('terminalZone', zone);
        else localStorage.removeItem('terminalZone');
        
        dispatch({ type: 'SET_TERMINAL_MODE', payload: { active, zone } });
    };

    /**
     * deactivateTerminalMode
     * Valida la contraseña del administrador antes de desactivar el bloqueo.
     */
    const deactivateTerminalMode = async (password) => {
        if (!state.user?.email) return false;
        
        try {
            const credential = EmailAuthProvider.credential(state.user.email, password);
            await reauthenticateWithCredential(auth.currentUser, credential);
            
            // Si la re-autenticación es exitosa, desactivamos el modo
            setTerminalMode(false);
            toast.success("Terminal unlocked successfully");
            return true;
        } catch (error) {
            console.error("Unlock error:", error);
            toast.error("Invalid administrator password");
            return false;
        }
    };

    /**
     * login
     * Autenticación estándar con email/password.
     */
    const login = async (email, password) => {
        dispatch({ type: 'AUTH_START' });
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            await syncUserWithFirestore(userCredential.user);
            return userCredential.user;
        } catch (error) {
            let message = "Error during login";
            if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                message = "Invalid email or password";
            }
            dispatch({ type: 'AUTH_ERROR', payload: message });
            toast.error(message);
            throw error;
        }
    };

    /**
     * register
     * Crea un nuevo usuario y su perfil en Firestore.
     */
    const register = async (email, password, name) => {
        dispatch({ type: 'AUTH_START' });
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            const userRef = doc(db, 'users', user.uid);
            const userData = {
                uid: user.uid,
                email: user.email,
                name: name || user.email.split('@')[0],
                role: 'MEMBER', // Por defecto
                createdAt: serverTimestamp()
            };
            
            await setDoc(userRef, userData);
            dispatch({ type: 'AUTH_SUCCESS', payload: userData });
            return user;
        } catch (error) {
            dispatch({ type: 'AUTH_ERROR', payload: error.message });
            toast.error(error.message);
            throw error;
        }
    };

    /**
     * logout
     * Cierra la sesión en Firebase y limpia el modo terminal.
     */
    const logout = async () => {
        try {
            // 1. Limpiar modo terminal primero (Seguridad)
            setTerminalMode(false);
            
            // 2. Cerrar sesión en Firebase
            await signOut(auth);
            
            // 3. Limpiar estado en memoria
            dispatch({ type: 'LOGOUT' });
        } catch (error) {
            toast.error("Error signing out");
        }
    };

    /**
     * syncUserWithFirestore
     * Obtiene el perfil enriquecido (roles) desde Firestore.
     */
    const syncUserWithFirestore = async (firebaseUser) => {
        if (!firebaseUser) return;
        
        try {
            const userRef = doc(db, 'users', firebaseUser.uid);
            const userSnap = await getDoc(userRef);
            
            let userData;
            if (userSnap.exists()) {
                userData = { ...userSnap.data(), uid: firebaseUser.uid, email: firebaseUser.email };
            } else {
                // New user: default to MEMBER
                userData = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    role: 'MEMBER', // DEFAULT
                    createdAt: serverTimestamp()
                };
                await setDoc(userRef, userData);
            }
            
            if (firebaseUser.email?.toLowerCase().includes('fromeros')) {
                userData.role = 'ADMIN';
            }

            // REMOVED AUTO-UNLOCK FOR ADMINS TO PERSIST TERMINAL MODE ON RELOAD
            
            dispatch({ type: 'AUTH_SUCCESS', payload: userData });
        } catch (error) {
            console.error("Error syncing user:", error);
            dispatch({ type: 'AUTH_ERROR', payload: "Failed to load user profile" });
        }
    };

    /**
     * resetPassword
     * Envía email de recuperación.
     */
    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
            toast.success("Password reset email sent!");
        } catch (error) {
            toast.error(error.message);
            throw error;
        }
    };

    // Observer de estado de sesión
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                syncUserWithFirestore(user);
            } else {
                dispatch({ type: 'LOGOUT' });
            }
        });
        return unsubscribe;
    }, []);

    return (
        <AuthContext.Provider value={{ 
            ...state, 
            login, 
            register, 
            logout, 
            resetPassword,
            setTerminalMode,
            deactivateTerminalMode 
        }}>
            {children}
        </AuthContext.Provider>
    );
};
